import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  collectAttachments,
  collectRemarks,
  extractFileDisplayName,
  extractFileKey,
  fetchIdCardMaster,
  findOneIdCard,
  formatSapDate,
  formatSapDateTime,
  getIdCardDocumentUrl,
  getStatusMeta,
  purposeText,
} from "./idCard.service";

const InfoRow: React.FC<{ label: string; value?: any }> = ({ label, value }) => (
  <div className="col-md-4 col-sm-6 mb-3">
    <span className="text-muted small d-block">{label}</span>
    <strong className="text-dark">{value || "-"}</strong>
  </div>
);

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">{title}</h5>
    </div>
    <div className="card-body">
      <div className="row">{children}</div>
    </div>
  </div>
);

const IDCardDetails: React.FC = () => {
  const { t } = useT();
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [masterOptions, setMasterOptions] = useState<any[]>([]);
  const [openingKey, setOpeningKey] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const [detail, master] = await Promise.all([
          findOneIdCard(id),
          fetchIdCardMaster().catch(() => []),
        ]);
        setData(detail);
        setMasterOptions(master);
      } catch (err: any) {
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, t]);

  const openFile = async (file: any) => {
    const key = extractFileKey(file);
    const direct = file?.url || file?.URL || file?.fileUrl;
    if (direct && /^https?:\/\//i.test(direct)) {
      window.open(direct, "_blank");
      return;
    }
    if (!key) {
      toast.error("Document key is missing.");
      return;
    }
    setOpeningKey(key);
    try {
      const url = await getIdCardDocumentUrl(key);
      if (url) window.open(url, "_blank");
      else toast.error("Unable to open file.");
    } finally {
      setOpeningKey("");
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-danger" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <h4 className="text-muted">{t("no_data_found") || "Request not found."}</h4>
          <button className="btn btn-outline-primary mt-3" onClick={() => navigate(-1)}>
            {t("go_back") || "Go Back"}
          </button>
        </div>
      </div>
    );
  }

  const meta = getStatusMeta(data.RequestStatus);
  const translated = t(meta.labelKey);
  const statusLabel = translated && translated !== meta.labelKey ? translated : meta.label;
  const remarks = collectRemarks(data);
  const attachments = collectAttachments(data);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center gap-2 mb-3">
          <button type="button" className="btn btn-link p-0" onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left fs-4" />
          </button>
          <h3 className="page-title mb-0">{t("id_card") || "ID Card"}</h3>
        </div>
        <nav className="mb-3">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to={routes.employeeDashboard}>Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={routes.idCard.list}>{t("see_previous_request")}</Link>
            </li>
            <li className="breadcrumb-item active">{t("view_details") || "Details"}</li>
          </ol>
        </nav>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className={`badge rounded-pill px-3 py-2 ${meta.cls}`}>{statusLabel}</span>
            <div className="text-end">
              <small className="text-muted d-block">{t("reference_no") || "Reference No."}</small>
              <strong>{data.ReferenceNumber || "-"}</strong>
            </div>
          </div>
        </div>

        <SectionCard title={t("request_information") || "Request Information"}>
          <InfoRow label={t("reference_no") || "Reference No."} value={data.ReferenceNumber} />
          <InfoRow
            label={t("request_creation_date") || "Request Creation Date"}
            value={formatSapDate(data.RequestCreationDate || data.created_at)}
          />
          <InfoRow label={t("employee_id") || "Employee ID"} value={data.EmpID} />
        </SectionCard>

        <SectionCard title={t("employee_details") || "Employee Details"}>
          <InfoRow label={t("employee_name") || "Employee Name"} value={data.EmployeeName} />
          <InfoRow label={t("designation") || "Designation"} value={data.Designation} />
          <InfoRow label={t("date_of_joining") || "Date of Joining"} value={formatSapDate(data.DateOfJoining)} />
          <InfoRow label={t("birth_date") || t("dob") || "Birth Date"} value={formatSapDate(data.BirthDate)} />
          <InfoRow label={t("blood_group")} value={data.BloodGroup} />
          <InfoRow label={t("contact_number") || "Contact Number"} value={data.ContactNumber} />
          <InfoRow label={t("place_of_posting") || t("Place_of_Posting")} value={data.PlaceOfPosting} />
        </SectionCard>

        <SectionCard title={t("request_details") || "Request Details"}>
          <InfoRow label={t("reason_to_apply") || "Reason to Apply"} value={purposeText(data.ReasonToApply, masterOptions)} />
          <InfoRow label={t("approver_level") || "Approver Level"} value={data.ApproverLevel} />
          <InfoRow label={t("Last_Latest_Approved") || t("last_latest_approved")} value={data.ApproverName} />
          <InfoRow label={t("ApproverUserID") || "Approver User ID"} value={data.ApproverUserID} />
        </SectionCard>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white">
            <h5 className="mb-0 fw-bold">{t("History") || "Remarks & Approval History"}</h5>
          </div>
          <div className="card-body">
            {remarks.length === 0 ? (
              <p className="text-muted mb-0">{t("no_data_found") || "No data found"}</p>
            ) : (
              remarks.map((r: any, idx: number) => (
                <div key={idx} className="border-bottom py-2">
                  <div className="d-flex justify-content-between">
                    <strong>{r.ApproverName || "-"}</strong>
                    <small className="text-muted">{formatSapDateTime(r.TimeStamp)}</small>
                  </div>
                  <div className="small text-muted">{r.ApproverUserID || r.ApproverLevel || ""}</div>
                  <div className="small">{r.HRemarks?.trim() || r.Remarks?.trim() || "-"}</div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white">
            <h5 className="mb-0 fw-bold">{t("attachments") || "Attachments"}</h5>
          </div>
          <div className="card-body">
            {attachments.length === 0 ? (
              <p className="text-muted mb-0">{t("no_documents_found") || "No documents found"}</p>
            ) : (
              attachments.map((item, idx) => {
                const key = extractFileKey(item.file);
                return (
                  <button
                    key={`${key}-${idx}`}
                    type="button"
                    className="btn btn-sm btn-outline-primary me-2 mb-2"
                    disabled={openingKey === key}
                    onClick={() => openFile(item.file)}
                  >
                    {openingKey === key ? (
                      <span className="spinner-border spinner-border-sm me-1" />
                    ) : (
                      <i className="ti ti-paperclip me-1" />
                    )}
                    {item.section}: {extractFileDisplayName(item.file)}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="alert alert-info d-flex align-items-center gap-2">
          <i className="ti ti-info-circle" />
          <span className="small">{t("readonly_notice") || "This is a read-only view of your request."}</span>
        </div>
      </div>
    </div>
  );
};

export default IDCardDetails;
